/**
 * Parent → game iframe postMessage bridge.
 *
 * Parent example:
 *   iframe.contentWindow.postMessage({ type: "stopAutoplay" }, "*");
 *
 * Add a new message later by:
 *   1. Writing a handleXxx() function
 *   2. Registering it in MESSAGE_HANDLERS
 */
import { getActiveGameStoreOrNull } from '@core/gameengine/state/activeGameStore';

const LOG_PREFIX = '[Post message]';

type BetSide = 'A' | 'B';

const BET_SIDES: BetSide[] = ['A', 'B'];

function log(message: string): void {
  console.log(`${LOG_PREFIX}: ${message}`);
}

function resolveAction(data: unknown): string | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const payload = data as { type?: unknown; name?: unknown };
  if (typeof payload.type === 'string') {
    return payload.type;
  }
  if (typeof payload.name === 'string') {
    return payload.name;
  }
  return null;
}

function getActiveAutoplaySides(state: {
  autoPlaySettings?: Record<BetSide, unknown>;
}): BetSide[] {
  const settings = state.autoPlaySettings;
  if (!settings) {
    return [];
  }
  return BET_SIDES.filter(side => settings[side] != null);
}

function getAutoplayStatusText(state: {
  autoPlaySettings?: Record<BetSide, unknown>;
}): string {
  if (!state.autoPlaySettings) {
    return 'game store not ready';
  }
  const activeSides = getActiveAutoplaySides(state);
  if (activeSides.length === 0) {
    return 'not playing';
  }
  return `playing (sides: ${activeSides.join(', ')})`;
}

/**
 * { type: "stopAutoplay" }
 * Stops autoplay on both bet sides if currently running.
 */
function handleStopAutoplay(): void {
  log('received post message - stop autoplay');

  const store = getActiveGameStoreOrNull();
  if (!store) {
    log('game store not ready - cannot stop autoplay');
    return;
  }

  const state = store.getState() as {
    autoPlaySettings?: Record<BetSide, unknown>;
    stopAutoPlay?: (side: BetSide) => void;
  };

  if (typeof state.stopAutoPlay !== 'function') {
    log('stopAutoPlay not available on current store - cannot stop autoplay');
    return;
  }

  log(`checking autoplay status - ${getAutoplayStatusText(state)}`);

  const activeSides = getActiveAutoplaySides(state);
  if (activeSides.length === 0) {
    log('autoplay is not playing - nothing to stop');
    return;
  }

  log(
    `autoplay is playing - stopping autoplay on side(s): ${activeSides.join(', ')}`
  );
  for (const side of activeSides) {
    state.stopAutoPlay(side);
  }
  log('stop autoplay command published successfully');
}

const MESSAGE_HANDLERS: Record<string, () => void> = {
  stopAutoplay: handleStopAutoplay,
};

function onMessage(event: MessageEvent): void {
  const action = resolveAction(event.data);
  if (!action) {
    return;
  }

  const handler = MESSAGE_HANDLERS[action];
  if (!handler) {
    return;
  }

  log(`routing post message - ${action}`);
  try {
    handler();
  } catch (err) {
    log(`error while handling post message - ${action} | ${err}`);
  }
}

let initialized = false;

/** Call once at app boot. Safe to call multiple times. */
export function initPostMessageHandler(): void {
  if (initialized) {
    return;
  }
  initialized = true;
  window.addEventListener('message', onMessage);
  log('listener initialized - waiting for parent messages');
}
